import { ClipboardList, PackageSearch } from "lucide-react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useId, useState, type ReactNode } from "react";
import {
  useEnrichedOrderDetail,
  useOrderSummaries,
} from "@/features/order/hooks";
import {
  isOperationalEnrichedOrder,
  isOperationalOrderSummaryPage,
  type OrderStatus,
  type OrderSummaryFilters,
} from "@/features/order/types";
import {
  formatIsoDateForDisplay,
  maskOrderDateForDisplay,
  parseDisplayDateToIso,
} from "@/features/order/filters";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { OperationalOrderCard } from "@/components/orders/OperationalOrderCard";
import { OrderStatusCheckboxGroup } from "@/components/orders/OrderStatusCheckboxGroup";
import { orderInputStyle } from "@/components/orders/orderStyles";
import { InlineLoadError } from "@/components/feedback/InlineLoadError";
import { OperationalFilterSurface } from "@/components/operational/OperationalFilterSurface";
import {
  OperationalPageFrame,
  OperationalPageHeader,
  OperationalToolbar,
} from "@/components/operational/OperationalPageLayout";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";

const PAGE_SIZE = 10;

type HelperOrderFilters = {
  search: string;
  statuses: OrderStatus[];
  date_from: string;
  date_to: string;
};

const DEFAULT_HELPER_FILTERS: HelperOrderFilters = {
  search: "",
  statuses: [],
  date_from: "",
  date_to: "",
};

function normalizeText(value: string) {
  return value.trim();
}

function normalizeHelperFilters(
  filters: HelperOrderFilters,
): HelperOrderFilters {
  return {
    search: normalizeText(filters.search),
    statuses: Array.from(new Set(filters.statuses)).sort(),
    date_from: filters.date_from,
    date_to: filters.date_to,
  };
}

function toOperationalQueryFilters(
  filters: HelperOrderFilters,
): OrderSummaryFilters | undefined {
  const normalized = normalizeHelperFilters(filters);
  const queryFilters = {
    ...(normalized.search ? { search: normalized.search } : {}),
    ...(normalized.statuses.length ? { statuses: normalized.statuses } : {}),
    ...(normalized.date_from ? { date_from: normalized.date_from } : {}),
    ...(normalized.date_to ? { date_to: normalized.date_to } : {}),
  };

  return Object.keys(queryFilters).length ? queryFilters : undefined;
}

function filtersEqual(left: HelperOrderFilters, right: HelperOrderFilters) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function activeFilterCount(filters: HelperOrderFilters) {
  return [
    filters.statuses.length > 0,
    Boolean(filters.date_from),
    Boolean(filters.date_to),
  ].filter(Boolean).length;
}

function useHelperOrderFilters() {
  const [draftFilters, setDraftFilters] = useState<HelperOrderFilters>(
    DEFAULT_HELPER_FILTERS,
  );
  const [appliedFilters, setAppliedFilters] = useState<HelperOrderFilters>(
    DEFAULT_HELPER_FILTERS,
  );
  const [offset, setOffset] = useState(0);

  function apply() {
    const normalized = normalizeHelperFilters(draftFilters);
    setDraftFilters(normalized);
    if (!filtersEqual(appliedFilters, normalized)) {
      setAppliedFilters(normalized);
    }
    setOffset(0);
  }

  function cancel() {
    setDraftFilters(appliedFilters);
  }

  function clear() {
    setDraftFilters(DEFAULT_HELPER_FILTERS);
    setAppliedFilters(DEFAULT_HELPER_FILTERS);
    setOffset(0);
  }

  return {
    draftFilters,
    setDraftFilters,
    appliedFilters,
    offset,
    setOffset,
    apply,
    cancel,
    clear,
  };
}

function getValidPageOffset(total: number, offset: number) {
  if (total <= 0 || offset < total) return offset;
  return Math.floor((total - 1) / PAGE_SIZE) * PAGE_SIZE;
}

function usePageOffsetClamp(
  total: number,
  offset: number,
  setOffset: (offset: number) => void,
) {
  const validOffset = getValidPageOffset(total, offset);
  const isCorrecting = validOffset !== offset;

  useEffect(() => {
    if (isCorrecting) setOffset(validOffset);
  }, [isCorrecting, setOffset, validOffset]);

  return { offset: validOffset, isCorrecting };
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="order-filter-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function QuotationDateFilterInput({
  label,
  value,
  resetVersion,
  onChange,
  onValidityChange,
}: {
  label: string;
  value: string;
  resetVersion: number;
  onChange: (value: string) => void;
  onValidityChange: (isInvalid: boolean) => void;
}) {
  const errorId = useId();
  const [displayValue, setDisplayValue] = useState(
    formatIsoDateForDisplay(value),
  );
  const trimmedDisplayValue = displayValue.trim();
  const isInvalid =
    Boolean(trimmedDisplayValue) && !parseDisplayDateToIso(trimmedDisplayValue);

  useEffect(() => {
    setDisplayValue(formatIsoDateForDisplay(value));
    onValidityChange(false);
  }, [resetVersion, value]);

  return (
    <>
      <input
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/AAAA"
        aria-label={label}
        aria-invalid={isInvalid || undefined}
        aria-describedby={isInvalid ? errorId : undefined}
        value={displayValue}
        onChange={(event) => {
          const nextDisplay = maskOrderDateForDisplay(event.target.value);
          const nextIso = parseDisplayDateToIso(nextDisplay);
          const nextInvalid = Boolean(nextDisplay.trim()) && !nextIso;
          setDisplayValue(nextDisplay);
          onValidityChange(nextInvalid);
          if (!nextInvalid) onChange(nextIso);
        }}
        style={orderInputStyle}
      />
      {isInvalid ? (
        <p id={errorId} role="alert" className="order-filter-field-alert">
          Use o formato DD/MM/AAAA.
        </p>
      ) : null}
    </>
  );
}

function OperationalListState({
  children,
  role,
  ariaLabel,
}: {
  children: ReactNode;
  role?: "alert" | "status";
  ariaLabel?: string;
}) {
  return (
    <div
      className="pedidos-ledger-state helper-operational-orders__state"
      role={role}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
}

export function AdminHelperOperationalOrdersView() {
  const navigate = useNavigate();
  const routeSearch = useSearch({ from: "/_auth/pedidos" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateInvalidity, setDateInvalidity] = useState({
    from: false,
    to: false,
  });
  const [dateResetVersion, setDateResetVersion] = useState(0);
  const filters = useHelperOrderFilters();
  const queryFilters = toOperationalQueryFilters(filters.appliedFilters);
  const selectedOrderId = routeSearch.orderId ?? null;
  const summariesQuery = useOrderSummaries({
    limit: PAGE_SIZE,
    offset: filters.offset,
    filters: queryFilters,
  });
  const summaryPage = summariesQuery.data;
  const detailQuery = useEnrichedOrderDetail(selectedOrderId);
  const isUnsafeResponse =
    (summaryPage && !isOperationalOrderSummaryPage(summaryPage)) ||
    (detailQuery.data && !isOperationalEnrichedOrder(detailQuery.data));
  const total = isOperationalOrderSummaryPage(summaryPage)
    ? summaryPage.count
    : 0;
  const detail = isOperationalEnrichedOrder(detailQuery.data)
    ? detailQuery.data
    : undefined;
  const pageOffset = usePageOffsetClamp(
    total,
    filters.offset,
    filters.setOffset,
  );
  const hasSummaryForPage =
    isOperationalOrderSummaryPage(summaryPage) &&
    summaryPage.offset === pageOffset.offset;
  const summaries = hasSummaryForPage ? summaryPage.items : [];
  const isAwaitingPageCorrection =
    isOperationalOrderSummaryPage(summaryPage) && !hasSummaryForPage;
  const selectedOutsideCurrentPage =
    selectedOrderId &&
    detail?.id === selectedOrderId &&
    !summaries.some((summary) => summary.id === selectedOrderId)
      ? detail
      : undefined;
  const displayedSummaries = selectedOutsideCurrentPage
    ? [selectedOutsideCurrentPage, ...summaries]
    : summaries;
  const appliedFilterCount = activeFilterCount(filters.appliedFilters);
  const listLoadError = summariesQuery.isError ? (
    <InlineLoadError
      title="Pedidos indisponíveis"
      message={getUserFacingErrorMessage(
        summariesQuery.error,
        "Não foi possível carregar pedidos operacionais.",
      )}
      onRetry={() => summariesQuery.refetch()}
      isRetrying={summariesQuery.isFetching}
    />
  ) : null;

  function setSelectedOrder(orderId: string | null) {
    navigate({
      to: "/pedidos" as string,
      search: orderId ? { orderId } : {},
    });
  }

  function toggleOrder(orderId: string) {
    setSelectedOrder(selectedOrderId === orderId ? null : orderId);
  }

  function clearSelection() {
    setSelectedOrder(null);
  }

  function applyFilters() {
    filters.apply();
    clearSelection();
  }

  function clearFilters() {
    setDateInvalidity({ from: false, to: false });
    setDateResetVersion((current) => current + 1);
    filters.clear();
    clearSelection();
  }

  return (
    <OperationalPageFrame
      size="standard"
      className="pedidos-page helper-operational-orders"
    >
      <OperationalPageHeader title="Pedidos" />
      <OperationalToolbar
        className="pedidos-toolbar"
        search={
          <OperationalSearchField
            value={filters.draftFilters.search}
            placeholder="Buscar cliente ou loja"
            ariaLabel="Buscar cliente ou loja"
            onChange={(search) =>
              filters.setDraftFilters((previous) => ({
                ...previous,
                search,
              }))
            }
          />
        }
        filters={
          <OperationalFilterSurface
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            title="Filtros"
            activeCount={appliedFilterCount}
            onApply={applyFilters}
            applyDisabled={dateInvalidity.from || dateInvalidity.to}
            onClear={clearFilters}
            onCancel={() => {
              setDateInvalidity({ from: false, to: false });
              filters.cancel();
            }}
          >
            <FilterField label="Status">
              <OrderStatusCheckboxGroup
                value={filters.draftFilters.statuses}
                onChange={(statuses) =>
                  filters.setDraftFilters((previous) => ({
                    ...previous,
                    statuses,
                  }))
                }
              />
            </FilterField>
            <FilterField label="Data da cotação (início)">
              <QuotationDateFilterInput
                label="Data da cotação (início)"
                value={filters.draftFilters.date_from}
                resetVersion={dateResetVersion}
                onChange={(date_from) =>
                  filters.setDraftFilters((previous) => ({
                    ...previous,
                    date_from,
                  }))
                }
                onValidityChange={(from) =>
                  setDateInvalidity((previous) => ({ ...previous, from }))
                }
              />
            </FilterField>
            <FilterField label="Data da cotação (fim)">
              <QuotationDateFilterInput
                label="Data da cotação (fim)"
                value={filters.draftFilters.date_to}
                resetVersion={dateResetVersion}
                onChange={(date_to) =>
                  filters.setDraftFilters((previous) => ({
                    ...previous,
                    date_to,
                  }))
                }
                onValidityChange={(to) =>
                  setDateInvalidity((previous) => ({ ...previous, to }))
                }
              />
            </FilterField>
          </OperationalFilterSurface>
        }
        status={
          summariesQuery.isFetching && summaries.length ? (
            <span role="status" aria-label="Atualizando pedidos">
              Atualizando…
            </span>
          ) : undefined
        }
      />
      {isUnsafeResponse ? (
        <OperationalListState role="alert">
          <p>Não foi possível exibir esta resposta de pedidos com segurança.</p>
        </OperationalListState>
      ) : !summaryPage ? (
        summariesQuery.isError ? (
          listLoadError
        ) : (
          <OperationalListState
            role="status"
            ariaLabel="Carregando pedidos operacionais"
          >
            <span
              className="helper-operational-orders__loading-icon"
              aria-hidden="true"
            >
              <ClipboardList size={18} />
            </span>
            Carregando pedidos operacionais…
          </OperationalListState>
        )
      ) : isAwaitingPageCorrection ? (
        <OperationalListState
          role="status"
          ariaLabel="Atualizando página de pedidos operacionais"
        >
          Atualizando página de pedidos operacionais…
        </OperationalListState>
      ) : !displayedSummaries.length ? (
        <OperationalListState>
          <PackageSearch size={22} aria-hidden="true" />
          <p>Nenhum pedido operacional encontrado.</p>
        </OperationalListState>
      ) : (
        <>
          {listLoadError}
          <div
            className="pedidos-ledger-list"
            aria-label="Lista operacional de pedidos"
            aria-busy={summariesQuery.isFetching || undefined}
          >
            {displayedSummaries.map((summary) => {
              const isExpanded = selectedOrderId === summary.id;

              return (
                <div
                  key={summary.id}
                  data-testid={`operational-order-card-${summary.id}`}
                >
                  <OperationalOrderCard
                    summary={summary}
                    detail={isExpanded ? detail : undefined}
                    isExpanded={isExpanded}
                    isLoading={isExpanded && detailQuery.isLoading}
                    isError={isExpanded && detailQuery.isError}
                    onToggle={() => toggleOrder(summary.id)}
                    onRetry={() => detailQuery.refetch()}
                  />
                </div>
              );
            })}
          </div>
          <OperationalPager
            variant="numbered"
            className="operational-pager--pedidos"
            totalItems={total}
            limit={PAGE_SIZE}
            offset={pageOffset.offset}
            isFetching={summariesQuery.isFetching}
            onPageChange={(page) => {
              filters.setOffset(page * PAGE_SIZE);
              clearSelection();
            }}
            ariaLabel="Paginação de pedidos operacionais"
            itemLabel="pedido"
            itemLabelPlural="pedidos"
          />
        </>
      )}
    </OperationalPageFrame>
  );
}
